/**
 * @fileoverview useWebAuthnUnlock Hook - Manages WebAuthn authentication for data decryption
 *
 * This hook provides state management for unlocking encrypted data using WebAuthn
 * credentials (biometric authentication). It handles both primary device unlock
 * (local key decryption) and linked device unlock (request forwarding).
 *
 * **Usage:**
 * ```tsx
 * const { unlock, isUnlocking, error } = useWebAuthnUnlock();
 *
 * const result = await unlock();
 * if (result.success) {
 *   // Access decrypted master key
 *   const masterKey = result.masterKey;
 * }
 * ```
 *
 * @module hooks/useWebAuthnUnlock
 */

import { useState, useCallback } from 'react';
import { createLogger } from '@xanectech/lib';
import { authenticateWithWebAuthn } from '../services/WebAuthnService';
import { listPrimaryDevices } from '../services/DeviceManagementService';
import {
  importMasterKey,
  initializeSessionWithMasterKey,
  validateRecoveryKey,
  decryptMasterKeyWithRecoveryKey,
} from '@xanectech/encryption';
import { supabase } from '@xanectech/supabase-client';
import { useAuth } from '../contexts/AuthContext';

const logger = createLogger('useWebAuthnUnlock');

export interface UnlockResult {
  success: boolean;
  masterKey?: CryptoKey;
  credentialId?: string;
  error?: string;
}

export function useWebAuthnUnlock() {
  const { user } = useAuth();
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string>('');

  /**
   * Unlock encrypted data with WebAuthn
   *
   * Prompts the user for biometric authentication and retrieves the master
   * encryption key if successful.
   *
   * @returns Unlock result with master key
   */
  const unlock = useCallback(async (): Promise<UnlockResult> => {
    if (!user) {
      const errorMsg = 'User must be authenticated to unlock';
      logger.error(errorMsg);
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }

    setIsUnlocking(true);
    setError(null);
    setProgress('Preparing to unlock...');

    try {
      logger.info('Starting WebAuthn unlock', { userId: user.id });

      // Step 1: Check if user has primary devices
      setProgress('Checking devices...');
      const primaryDevices = await listPrimaryDevices(user.id);

      if (primaryDevices.length === 0) {
        throw new Error(
          'No primary devices found. Please set up WebAuthn first.',
        );
      }

      // Step 2: Authenticate with WebAuthn
      setProgress('Requesting biometric authentication...');
      const authResult = await authenticateWithWebAuthn({
        userId: user.id,
      });

      if (!authResult.success) {
        logger.error(
          'WebAuthn authentication failed',
          {
            error: authResult.error,
          },
          'auth',
        );
        throw new Error(authResult.error || 'Authentication failed');
      }

      logger.info('WebAuthn authentication successful', {
        credentialId: authResult.credentialId,
      });

      // Step 3: Use the wrapped key returned from authentication verification
      if (!authResult.wrappedKey) {
        throw new Error('Failed to unwrap encryption key');
      }

      logger.info('Master key unwrapped successfully');

      // Step 4: Convert the unwrapped key (base64 string) to raw bytes

      setProgress('Processing encryption key...');
      const unwrappedKeyString = authResult.wrappedKey.encryptedMasterKey;

      const decodeBase64ToBytes = (value: string): Uint8Array => {
        try {
          const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
          const padded = normalized.padEnd(
            normalized.length + ((4 - (normalized.length % 4)) % 4),
            '=',
          );
          return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
        } catch (error) {
          logger.error(
            'Failed to base64 decode unwrapped key, falling back to UTF-8 encode',
            error as Error,
          );
          return new TextEncoder().encode(value);
        }
      };

      const masterKeyMaterial = decodeBase64ToBytes(String(unwrappedKeyString));

      // Step 5: Import the master key bytes to create a CryptoKey
      setProgress('Initializing session...');
      const masterKey = await importMasterKey(masterKeyMaterial);

      // Step 6: Initialize the encryption session with the master key
      await initializeSessionWithMasterKey(user.id, masterKey);

      setProgress('Unlock complete!');
      setIsUnlocking(false);

      logger.info(
        'WebAuthn unlock successful - encryption session initialized',
        {
          credentialId: authResult.credentialId,
        },
      );

      return {
        success: true,
        masterKey,
        credentialId: authResult.credentialId,
      };
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unlock failed';
      logger.error('WebAuthn unlock failed', err as Error);
      setError(errorMsg);
      setIsUnlocking(false);
      setProgress('');
      return { success: false, error: errorMsg };
    }
  }, [user]);

  /**
   * Unlock with password fallback
   *
   * Not implemented - WebAuthn biometric is the only unlock method.
   *
   * @param _password - Unused
   * @returns Error result
   */
  const unlockWithPassword = useCallback(
    async (_password: string): Promise<UnlockResult> => {
      const errorMsg = 'Use biometric unlock instead';
      logger.error(errorMsg);
      setError(errorMsg);
      return {
        success: false,
        error: errorMsg,
      };
    },
    [],
  );

  /**
   * Unlock with recovery key
   *
   * Uses recovery_wrapped_key from user_master_keys to restore the master key
   * when WebAuthn credentials are unavailable.
   *
   * @param recoveryPhrase - 24-word recovery phrase
   * @returns Unlock result
   */
  const unlockWithRecoveryKey = useCallback(
    async (recoveryPhrase: string): Promise<UnlockResult> => {
      if (!user) {
        const errorMsg = 'User must be authenticated to unlock';
        logger.error(errorMsg);
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }

      const normalizedPhrase = recoveryPhrase.trim().toLowerCase();
      if (!validateRecoveryKey(normalizedPhrase)) {
        const errorMsg = 'Invalid recovery key. Please verify all 24 words.';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }

      setIsUnlocking(true);
      setError(null);
      setProgress('Validating recovery key...');

      try {
        const { data: masterKeyData, error: masterKeyError } = await supabase
          .from('user_master_keys')
          .select('recovery_wrapped_key')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .maybeSingle();

        if (masterKeyError) {
          throw new Error(masterKeyError.message);
        }

        if (!masterKeyData?.recovery_wrapped_key) {
          throw new Error(
            'Recovery key unlock is not configured for this account.',
          );
        }

        const wrapped = masterKeyData.recovery_wrapped_key as Record<
          string,
          string
        >;

        if (!wrapped.encryptedKey || !wrapped.iv || !wrapped.salt) {
          throw new Error('Recovery key data is invalid.');
        }

        setProgress('Decrypting recovery key material...');
        const masterKeyMaterial = await decryptMasterKeyWithRecoveryKey(
          {
            encryptedKey: wrapped.encryptedKey,
            iv: wrapped.iv,
            salt: wrapped.salt,
            algorithm: wrapped.algorithm || 'AES-256-GCM',
          },
          normalizedPhrase,
        );

        setProgress('Initializing session...');
        const masterKey = await importMasterKey(masterKeyMaterial);
        await initializeSessionWithMasterKey(user.id, masterKey);

        setProgress('Unlock complete!');
        setIsUnlocking(false);

        return {
          success: true,
          masterKey,
        };
      } catch (err) {
        const errorMsg =
          err instanceof Error
            ? err.message
            : 'Failed to unlock with recovery key';
        logger.error('Recovery key unlock failed', err as Error);
        setError(errorMsg);
        setIsUnlocking(false);
        setProgress('');
        return { success: false, error: errorMsg };
      }
    },
    [user],
  );

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    unlock,
    unlockWithPassword,
    unlockWithRecoveryKey,
    isUnlocking,
    error,
    progress,
    clearError,
  };
}
